/**
 * Sidebar — primary navigation surface for the app shell.
 *
 * Layout
 *   ┌──────────────────────┐
 *   │  brand row (h-16)    │  — logo + optional collapse toggle
 *   ├──────────────────────┤
 *   │  group row           │  — icon + label + chevron (collapsible)
 *   │    ↳ item row        │  — indented, violet accent when active
 *   │    ↳ item row        │
 *   │  group row           │
 *   │  ...                 │
 *   ├──────────────────────┤
 *   │  user avatar row     │  — opens dropdown (Profile / Settings / Logout)
 *   └──────────────────────┘
 *
 * Behaviour
 *   • `collapsed` shrinks the sidebar to just icons (w-sidebar-collapsed).
 *     A small circular chevron button on the right border toggles it, and
 *     the parent (<AppShell/>) is responsible for persisting the state to
 *     localStorage.
 *   • `inDrawer` is passed by the mobile Sheet: the collapse toggle is
 *     hidden and the width is fluid (100% of the drawer).
 *   • Nav data is imported from `@/shared/nav` and filtered by the current
 *     session's `enabledModules`.
 *   • Top-level `NavGroup`s render as expandable rows. Each group tracks its
 *     open/closed state independently, persisted per-user under the key
 *     `nav:expanded:{groupName}:{userId}` so a manufacturer profile and a
 *     retail profile on the same browser don't step on each other. A group
 *     containing the currently-active route auto-expands (even if the user
 *     hasn't touched it yet) so browsing the app never leaves the operator
 *     staring at a bunch of closed accordions.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ElementRef,
} from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Compass,
  LogOut,
  Search,
  Settings,
  User as UserIcon,
  X,
} from 'lucide-react';
import { NAV, filterNav, type NavGroup, type NavItem, type NavSubgroup, type NavAccent } from '@/shared/nav';
import { useAuth } from '@/shared/auth/store';
import { useLogout } from '@/shared/auth/useLogin';
import { useCurrentCompany } from '@/shared/auth/use-current-company';
import { resolveUploadUrl } from '@/shared/api/uploads';
import { GettingStarted } from '@/shared/getting-started';
import { useTenantReadiness } from '@/shared/features/tenant-readiness-card';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Badge } from './badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { OmeecronLogo } from './omeecron-brand';
import { cn } from './utils';

/** LocalStorage key prefix. Full key: `nav:expanded:{groupLabel}:{userId}`. */
const EXPANDED_KEY_PREFIX = 'nav:expanded';
/**
 * LocalStorage key prefix for the second-level sub-dropdown state.
 * Full key: `nav:sub:{groupLabel}::{subgroupLabel}:{userId}`. Keeping this
 * distinct from the top-level key prevents a subgroup called "Setup" from
 * clashing with a top-level group of the same name.
 */
const SUBGROUP_KEY_PREFIX = 'nav:sub';

const expandedKey = (groupLabel: string, userId: number | string): string =>
  `${EXPANDED_KEY_PREFIX}:${groupLabel}:${userId}`;

const subgroupKey = (
  groupLabel: string,
  subgroupLabel: string,
  userId: number | string,
): string => `${SUBGROUP_KEY_PREFIX}:${groupLabel}::${subgroupLabel}:${userId}`;

/**
 * Per-section accent palette — the reference-app pattern that lets a
 * reader tell Sell / Buy / Money apart at a glance. Each entry is a
 * static object of Tailwind classes so Tailwind's JIT scanner can pick
 * them up (dynamic string concatenation would be tree-shaken away).
 */
interface AccentClasses {
  icon: string;
  iconActive: string;
  bgHover: string;
  bgActive: string;
  textActive: string;
  ringActive: string;
}

const ACCENT_MAP: Record<NavAccent, AccentClasses> = {
  sky:     { icon: 'text-sky-500',     iconActive: 'text-sky-700',     bgHover: 'hover:bg-sky-50',     bgActive: 'bg-sky-50',     textActive: 'text-sky-800',     ringActive: 'before:bg-sky-500' },
  emerald: { icon: 'text-emerald-500', iconActive: 'text-emerald-700', bgHover: 'hover:bg-emerald-50', bgActive: 'bg-emerald-50', textActive: 'text-emerald-800', ringActive: 'before:bg-emerald-500' },
  amber:   { icon: 'text-amber-500',   iconActive: 'text-amber-700',   bgHover: 'hover:bg-amber-50',   bgActive: 'bg-amber-50',   textActive: 'text-amber-800',   ringActive: 'before:bg-amber-500' },
  rose:    { icon: 'text-rose-500',    iconActive: 'text-rose-700',    bgHover: 'hover:bg-rose-50',    bgActive: 'bg-rose-50',    textActive: 'text-rose-800',    ringActive: 'before:bg-rose-500' },
  violet:  { icon: 'text-violet-500',  iconActive: 'text-violet-700',  bgHover: 'hover:bg-violet-50',  bgActive: 'bg-violet-50',  textActive: 'text-violet-800',  ringActive: 'before:bg-violet-500' },
  cyan:    { icon: 'text-cyan-500',    iconActive: 'text-cyan-700',    bgHover: 'hover:bg-cyan-50',    bgActive: 'bg-cyan-50',    textActive: 'text-cyan-800',    ringActive: 'before:bg-cyan-500' },
  teal:    { icon: 'text-teal-500',    iconActive: 'text-teal-700',    bgHover: 'hover:bg-teal-50',    bgActive: 'bg-teal-50',    textActive: 'text-teal-800',    ringActive: 'before:bg-teal-500' },
  indigo:  { icon: 'text-indigo-500',  iconActive: 'text-indigo-700',  bgHover: 'hover:bg-indigo-50',  bgActive: 'bg-indigo-50',  textActive: 'text-indigo-800',  ringActive: 'before:bg-indigo-500' },
  pink:    { icon: 'text-pink-500',    iconActive: 'text-pink-700',    bgHover: 'hover:bg-pink-50',    bgActive: 'bg-pink-50',    textActive: 'text-pink-800',    ringActive: 'before:bg-pink-500' },
  orange:  { icon: 'text-orange-500',  iconActive: 'text-orange-700',  bgHover: 'hover:bg-orange-50', bgActive: 'bg-orange-50', textActive: 'text-orange-800', ringActive: 'before:bg-orange-500' },
  slate:   { icon: 'text-slate-500',   iconActive: 'text-slate-700',   bgHover: 'hover:bg-slate-100',  bgActive: 'bg-slate-100',  textActive: 'text-slate-800',   ringActive: 'before:bg-slate-500' },
};

const DEFAULT_ACCENT: AccentClasses = ACCENT_MAP.sky;
function accentFor(key?: NavAccent): AccentClasses {
  return key ? ACCENT_MAP[key] ?? DEFAULT_ACCENT : DEFAULT_ACCENT;
}

export interface SidebarProps {
  /** Icons-only mode. Ignored when `inDrawer` is true. */
  collapsed?: boolean;
  /** Toggle handler for the chevron collapse button. */
  onToggleCollapsed?: () => void;
  /** Rendered inside a mobile Sheet drawer — hides the collapse chrome. */
  inDrawer?: boolean;
  className?: string;
}

export const Sidebar = forwardRef<ElementRef<'aside'>, SidebarProps>(
  ({ collapsed = false, onToggleCollapsed, inDrawer = false, className }, ref) => {
    const user = useAuth((s) => s.user);
    const roles = useAuth((s) => s.roles);
    const enabledModules = useAuth((s) => s.enabledModules);
    const installAdmin = useAuth((s) => s.installAdmin);
    const logout = useLogout();
    const navigate = useNavigate();
    const location = useLocation();

    // Drawer mode always shows full-width labels — the drawer already sizes us.
    const isCompact = collapsed && !inDrawer;

    const groups = useMemo(
      () => filterNav(NAV, enabledModules, roles, { installAdmin }),
      [enabledModules, roles, installAdmin],
    );

    /* ------------------------------- search -------------------------------- */
    // A plain in-place filter, not a duplicate of the ⌘K command palette —
    // this one shows results inline in the sidebar itself so a non-technical
    // user browsing by eye (not a keyboard shortcut they may not know about)
    // can still type "raw material" or "GRN" and jump straight to it.
    const [query, setQuery] = useState('');
    const trimmedQuery = query.trim().toLowerCase();
    // Clear the sidebar search box whenever the route changes. Without this
    // the last search text (and its result list) lingers under the search
    // input every time the user navigates — even after they clicked a
    // suggestion, opened a group link, or landed here from the topbar.
    useEffect(() => {
      setQuery('');
    }, [location.pathname]);
    const searchResults = useMemo(() => {
      if (!trimmedQuery) return [];
      const results: Array<{ item: NavItem; groupLabel: string }> = [];
      for (const group of groups) {
        for (const item of group.items) {
          const haystack = `${item.label} ${item.description ?? ''}`.toLowerCase();
          if (haystack.includes(trimmedQuery)) {
            results.push({ item, groupLabel: group.label });
          }
        }
      }
      return results;
    }, [groups, trimmedQuery]);

    /* ---------------------------- active route ---------------------------- */

    const pathname = location.pathname;

    // Longest-prefix-wins, single winner across the WHOLE nav tree. Testing
    // each item independently (`pathname.startsWith(item.to)`) lets siblings
    // that share a route segment — or any future nesting like a detail page
    // under a list route — light up together, which is exactly the kind of
    // "every item looks selected" mess a sidebar must never show. Resolving
    // one winner up front and comparing by identity guarantees exactly one
    // item (and therefore exactly one group/subgroup) is ever marked active.
    const allNavItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
    const activeItemPath = useMemo(() => {
      const exact = allNavItems.find((it) => it.to === pathname);
      if (exact) return exact.to;
      let best: string | null = null;
      let bestLen = -1;
      for (const it of allNavItems) {
        const p = it.to;
        if (p === '/dashboard' || p === '/') continue; // exact-only, handled above
        if (pathname === p || pathname.startsWith(p + '/')) {
          if (p.length > bestLen) { bestLen = p.length; best = p; }
        }
      }
      return best;
    }, [allNavItems, pathname]);

    const isRouteActive = useCallback(
      (to: string) => to === activeItemPath,
      [activeItemPath],
    );

    /* --------------------------- expanded state --------------------------- */
    // One open/closed flag per group, keyed by user so profiles on the same
    // browser stay independent. Groups start closed unless the current route
    // lives inside them, or the group declares `defaultExpanded`.
    const userKey: number | string = user?.id ?? 'anon';

    const activeGroupLabel = useMemo(() => {
      const owner =
        groups.find((g) => g.items.some((i) => i.to === pathname)) ??
        groups.find((g) => g.items.some((i) => isRouteActive(i.to)));
      return owner?.label ?? null;
    }, [groups, pathname, isRouteActive]);

    const [openMap, setOpenMap] = useState<Record<string, boolean>>(() =>
      buildInitialOpenMap(NAV, userKey, activeGroupLabel),
    );

    // Reset the map whenever the active user changes — a logout / tenant
    // switch shouldn't inherit the previous account's expanded groups.
    const lastUserKey = useRef(userKey);
    useEffect(() => {
      if (lastUserKey.current === userKey) return;
      lastUserKey.current = userKey;
      setOpenMap(buildInitialOpenMap(NAV, userKey, activeGroupLabel));
    }, [userKey, activeGroupLabel]);

    // Auto-expand the group holding the current route. We only *open* here —
    // never close a user's deliberately-collapsed group they're still on.
    // Guarded on pathname so toggling a group inside the active group doesn't
    // ping-pong it back open on the next render. Accordion-consistent: when
    // we open the active group, we also collapse every OTHER group so the
    // sidebar never sprawls after a navigation.
    const lastPath = useRef<string | null>(null);
    useEffect(() => {
      if (lastPath.current === pathname) return;
      lastPath.current = pathname;
      if (!activeGroupLabel) return;
      setOpenMap((prev) => {
        if (prev[activeGroupLabel]) return prev;
        const next: Record<string, boolean> = {};
        for (const key of Object.keys(prev)) next[key] = false;
        next[activeGroupLabel] = true;
        try {
          for (const key of Object.keys(next)) {
            window.localStorage.setItem(expandedKey(key, userKey), String(next[key]));
          }
        } catch {
          /* localStorage may be blocked — ignore. */
        }
        return next;
      });
    }, [pathname, activeGroupLabel, userKey]);

    const toggleGroup = useCallback(
      (label: string) => {
        setOpenMap((prev) => {
          const wasOpen = !!prev[label];
          // Accordion behaviour: opening a group collapses every other
          // top-level group so the sidebar never grows into an unbrowsable
          // wall of expanded lists. Closing a group leaves the rest as-is —
          // the operator explicitly asked for a smaller sidebar.
          const next: Record<string, boolean> = {};
          if (wasOpen) {
            // Close the tapped group; keep the others where they were.
            for (const key of Object.keys(prev)) next[key] = !!prev[key];
            next[label] = false;
          } else {
            // Open the tapped group; force everything else closed.
            for (const key of Object.keys(prev)) next[key] = false;
            next[label] = true;
          }
          try {
            for (const key of Object.keys(next)) {
              window.localStorage.setItem(expandedKey(key, userKey), String(next[key]));
            }
          } catch {
            /* localStorage may be blocked (private mode, quota) — ignore. */
          }
          return next;
        });
      },
      [userKey],
    );

    /* -------------------------- getting-started --------------------------- */
    // The "New here? Show me around" modal is triggered from the pinned link
    // just above the user avatar; state lives at the sidebar level so both
    // the compact and drawer layouts share the same modal instance.
    const [tourOpen, setTourOpen] = useState(false);

    // Readiness gauge — drives the small numeric badge on the Compass button.
    // The card on the dashboard and the inline banners on each setting page
    // share the same query (so we're not paying for a second network round-trip).
    const { data: readiness } = useTenantReadiness();
    const readinessRemaining = readiness?.remaining ?? 0;

    // Same source as the topbar's Company Switcher — a small card here
    // reminds the operator which company the app is scoped to right now.
    // Rendered only when a company is actually selected; hidden entirely
    // in the compact (icons-only) sidebar.
    const { company: currentCompany } = useCurrentCompany();

    return (
      <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <aside
        ref={ref}
        aria-label="Primary navigation"
        className={cn(
          // Sticky itself establishes a containing block for the floating
          // collapse chevron (position: absolute inside), so no `relative` needed.
          'sticky top-0 z-30 flex h-screen shrink-0 flex-col',
          'bg-sidebar text-sidebar-foreground border-r border-sidebar-border',
          'transition-[width] duration-200 ease-out',
          inDrawer
            ? 'w-full'
            : isCompact
              ? 'w-sidebar-collapsed'
              : 'w-sidebar',
          className,
        )}
      >
        {/* Floating collapse toggle — sits on the right border like Notion/Linear. */}
        {!inDrawer && onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'absolute top-[1.375rem] -right-3 z-40',
              'hidden h-6 w-6 items-center justify-center rounded-full',
              'border border-sidebar-border bg-card text-muted-foreground shadow-sm',
              'transition-colors hover:text-foreground focus-ring',
              'lg:inline-flex',
            )}
          >
            {collapsed
              ? <ChevronsRight className="size-3.5" />
              : <ChevronsLeft className="size-3.5" />
            }
          </button>
        )}

        {/* Brand */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center gap-2 border-b border-sidebar-border',
            isCompact ? 'justify-center px-2' : 'px-4',
          )}
        >
          <Link
            to="/dashboard"
            className={cn(
              'flex min-w-0 items-center gap-2 rounded-md focus-ring',
              isCompact ? 'justify-center' : '',
            )}
            aria-label="Omeecron ERP — go to dashboard"
          >
            {/* The product mark, not a placeholder letter. Every install of
                this ships to a client who sees this corner on every screen;
                "E" for "ERP Demo" told them they were looking at somebody
                else's development build. */}
            <OmeecronLogo size={32} className="rounded-lg" />
            {!isCompact && (
              <span className="truncate text-sm font-semibold text-foreground">
                Omeecron ERP
              </span>
            )}
          </Link>
        </div>

        {/* Search — collapsed sidebar skips it entirely, there's no room to
            usefully show results in icons-only mode. */}
        {!isCompact && (
          <div className="border-b border-sidebar-border px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the menu…"
                aria-label="Search the menu"
                className={cn(
                  'h-8 w-full rounded-md border border-sidebar-border bg-sidebar pl-8 pr-7 text-xs',
                  'text-sidebar-foreground placeholder:text-muted-foreground',
                  'focus-ring focus:border-primary/50',
                )}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3">
          {trimmedQuery ? (
            <ul className="flex flex-col gap-0.5 px-2">
              {searchResults.length === 0 ? (
                <li className="px-2 py-4 text-center text-xs text-muted-foreground">
                  No matches for "{query.trim()}".
                </li>
              ) : (
                searchResults.map(({ item, groupLabel }) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={() => setQuery('')}
                      className={cn(
                        'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                        'text-sidebar-foreground hover:bg-sidebar-hover',
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{item.label}</span>
                        <span className="block truncate text-[10px] text-muted-foreground">{groupLabel}</span>
                      </span>
                    </NavLink>
                  </li>
                ))
              )}
            </ul>
          ) : (
            <ul className="flex flex-col gap-0.5 px-2">
              {groups.map((group) => {
                const hasActiveChild = group.items.some((i) => isRouteActive(i.to));
                const isOpen = !!openMap[group.label];
                return (
                  <SidebarGroupRow
                    key={group.label}
                    group={group}
                    isOpen={isOpen}
                    hasActiveChild={hasActiveChild}
                    compact={isCompact}
                    onToggle={() => toggleGroup(group.label)}
                    isRouteActive={isRouteActive}
                    userKey={userKey}
                  />
                );
              })}
            </ul>
          )}
        </nav>

        {/* "New here?" — quick tour launcher, pinned just above the user row. */}
        <div className={cn('mt-auto border-t border-sidebar-border', isCompact ? 'p-1.5' : 'p-2')}>
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            aria-label="New here? Open a quick tour of Omeecron ERP"
            title={isCompact ? 'New here? Show me around' : undefined}
            className={cn(
              'group flex w-full items-center gap-2 rounded-md text-xs font-medium transition-colors focus-ring',
              'text-muted-foreground hover:bg-sidebar-hover hover:text-foreground',
              isCompact ? 'justify-center p-1.5' : 'px-2 py-1.5',
            )}
          >
            <Compass className="size-4 shrink-0" aria-hidden />
            {!isCompact && <span className="truncate">New here? Show me around</span>}
            {readinessRemaining > 0 && (
              <Badge
                variant="warning"
                size="sm"
                className={cn('shrink-0 tabular-nums', isCompact && 'ml-0')}
                title={`${readinessRemaining} setup step${readinessRemaining === 1 ? '' : 's'} still to complete`}
                aria-label={`${readinessRemaining} setup steps remaining`}
              >
                {readinessRemaining}
              </Badge>
            )}
          </button>

          {/* Currently-active company chip. Same query as the topbar's
              switcher — the topbar owns the picker; this is a passive
              reminder so the operator can see the scope at a glance
              without looking up. Hidden in icons-only mode. */}
          {!isCompact && currentCompany && (
            <div
              className={cn(
                'mt-1 flex items-center gap-2 rounded-md border border-sidebar-border/70',
                'bg-sidebar-hover/40 px-2 py-1.5',
              )}
              title={`${currentCompany.name}${currentCompany.industry_label ? ' — ' + currentCompany.industry_label : ''}`}
            >
              <Building2
                className="size-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-foreground">
                  {currentCompany.name}
                </div>
                {currentCompany.industry_label && (
                  <div className="truncate text-2xs text-muted-foreground">
                    {currentCompany.industry_label}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pinned user */}
        <div className="border-t border-sidebar-border p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open account menu (sidebar)"
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg text-left transition-colors',
                  'text-foreground/90 hover:bg-sidebar-hover focus-ring',
                  isCompact ? 'justify-center p-1.5' : 'px-2 py-1.5',
                )}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  {user?.avatar_path && (
                    <AvatarImage src={resolveUploadUrl(user.avatar_path)} alt={user?.name ?? ''} />
                  )}
                  <AvatarFallback name={user?.name ?? 'User'} />
                </Avatar>
                {!isCompact && (
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {user?.name ?? 'Guest'}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {roleForDisplay(roles)}
                    </div>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" sideOffset={8} className="w-60">
              <div className="flex flex-col gap-0.5 px-2 py-1.5">
                <span className="truncate text-sm font-medium text-foreground">
                  {user?.name ?? 'Account'}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user?.email ?? ''}
                </span>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/profile')}>
                <UserIcon />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/admin/numbering')}>
                <Settings />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => logout.mutate()}
                className="text-destructive focus:text-destructive"
              >
                <LogOut />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      <GettingStarted open={tourOpen} onOpenChange={setTourOpen} />
      </TooltipProvider>
    );
  },
);
Sidebar.displayName = 'Sidebar';

/* ------------------------------------------------------------------ */
/*  Rows                                                              */
/* ------------------------------------------------------------------ */

interface SidebarGroupRowProps {
  group: NavGroup;
  isOpen: boolean;
  hasActiveChild: boolean;
  compact: boolean;
  onToggle: () => void;
  isRouteActive: (to: string) => boolean;
  /** User id (or 'anon') — threaded through so subgroup state persists per profile. */
  userKey: number | string;
}

function SidebarGroupRow({
  group,
  isOpen,
  hasActiveChild,
  compact,
  onToggle,
  isRouteActive,
  userKey,
}: SidebarGroupRowProps) {
  const Icon = group.icon;
  const accent = accentFor(group.accent);

  // Look up items by path once so the subgroup renderer can pluck them out
  // in O(1). Any item not referenced by a subgroup renders "loose" at the
  // top of the group so nothing gets silently hidden if a subgroup mapping
  // drops behind the flat items list.
  const itemsByPath = useMemo(() => {
    const map = new Map<string, NavItem>();
    for (const it of group.items) map.set(it.to, it);
    return map;
  }, [group.items]);

  const subgroups = group.subgroups ?? [];
  const looseItems = useMemo(() => {
    if (subgroups.length === 0) return group.items;
    const claimed = new Set(subgroups.flatMap((sg) => sg.itemPaths));
    return group.items.filter((it) => !claimed.has(it.to));
  }, [group.items, subgroups]);

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        title={compact ? group.label : group.description}
        className={cn(
          'group flex h-8 w-full items-center gap-2.5 rounded-lg py-1.5 text-[13px] font-medium uppercase tracking-wider transition-colors focus-ring',
          'text-foreground/80',
          accent.bgHover,
          'hover:text-foreground',
          hasActiveChild && cn('text-foreground', accent.textActive),
          compact ? 'justify-center px-0' : 'px-3',
        )}
      >
        <Icon className={cn('size-4 shrink-0', accent.icon)} aria-hidden />
        {!compact && (
          <>
            <span className="flex-1 truncate text-left">{group.label}</span>
            {isOpen
              ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              : <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            }
          </>
        )}
      </button>
      {isOpen && !compact && (
        <ul className="mb-1 ml-3.5 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2">
          {looseItems.map((item) => (
            <SidebarItemRow
              key={item.to}
              item={item}
              active={isRouteActive(item.to)}
              accent={accent}
            />
          ))}
          {subgroups.map((sub) => (
            <SidebarSubgroupRow
              key={sub.label}
              groupLabel={group.label}
              subgroup={sub}
              itemsByPath={itemsByPath}
              isRouteActive={isRouteActive}
              userKey={userKey}
              accent={accent}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface SidebarSubgroupRowProps {
  groupLabel: string;
  subgroup: NavSubgroup;
  itemsByPath: Map<string, NavItem>;
  isRouteActive: (to: string) => boolean;
  userKey: number | string;
  accent: AccentClasses;
}

/**
 * Second-level collapsible row inside a group. Persists its open state per
 * user so a profile that never touches Setup doesn't have Company & Access
 * pop open on every visit.
 */
function SidebarSubgroupRow({
  groupLabel,
  subgroup,
  itemsByPath,
  isRouteActive,
  userKey,
  accent,
}: SidebarSubgroupRowProps) {
  const Icon = subgroup.icon;
  const items = useMemo(
    () =>
      subgroup.itemPaths
        .map((p) => itemsByPath.get(p))
        .filter((i): i is NavItem => !!i),
    [subgroup.itemPaths, itemsByPath],
  );

  // Auto-open when the currently-active route sits inside this subgroup —
  // same principle as the top-level group but scoped one level deeper.
  const hasActiveChild = items.some((i) => isRouteActive(i.to));

  const [open, setOpen] = useState<boolean>(() => {
    const stored = readSubgroupPref(groupLabel, subgroup.label, userKey);
    if (stored !== undefined) return stored;
    return hasActiveChild;
  });

  // Same one-shot open-on-nav dance as top-level groups.
  const lastActive = useRef(hasActiveChild);
  useEffect(() => {
    if (lastActive.current === hasActiveChild) return;
    lastActive.current = hasActiveChild;
    if (hasActiveChild && !open) setOpen(true);
  }, [hasActiveChild, open]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(subgroupKey(groupLabel, subgroup.label, userKey), String(next));
      } catch {
        /* localStorage blocked — ignore. */
      }
      return next;
    });
  }, [groupLabel, subgroup.label, userKey]);

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        title={subgroup.description}
        className={cn(
          'flex h-7 w-full items-center gap-2 rounded-md px-2 text-xs font-semibold uppercase tracking-wide transition-colors focus-ring',
          'text-muted-foreground hover:text-foreground',
          accent.bgHover,
          hasActiveChild && cn('text-foreground', accent.textActive),
        )}
      >
        {Icon
          ? <Icon className={cn('size-3.5 shrink-0 opacity-80', accent.icon)} aria-hidden />
          : <span className="size-3.5 shrink-0" aria-hidden />
        }
        <span className="flex-1 truncate text-left">{subgroup.label}</span>
        {open
          ? <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
          : <ChevronRight className="size-3.5 shrink-0 opacity-60" aria-hidden />
        }
      </button>
      {open && (
        <ul className="mb-1 ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border/70 pl-2">
          {items.map((item) => (
            <SidebarItemRow
              key={item.to}
              item={item}
              active={isRouteActive(item.to)}
              accent={accent}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface SidebarItemRowProps {
  item: NavItem;
  active: boolean;
  /** Group accent — used to tint the active pill + left ring per section. */
  accent?: AccentClasses;
}

function SidebarItemRow({ item, active, accent }: SidebarItemRowProps) {
  const Icon = item.icon;
  const end = item.to === '/dashboard' || item.to === '/';
  const a = accent ?? DEFAULT_ACCENT;

  const link = (
    <NavLink
      to={item.to}
      end={end}
      // Deliberately ignore NavLink's own `isActive` — react-router matches
      // by simple prefix with no tiebreak, so it would independently light
      // up every sibling under a shared segment. `active` is the single,
      // longest-prefix-resolved winner computed once for the whole tree.
      className={cn(
        // `relative` so the left accent bar can absolutely-position itself.
        'group/nav relative flex h-7 items-center gap-2.5 rounded-md border border-transparent pl-3 pr-3 py-1 text-sm transition-colors focus-ring',
        'text-foreground/75 hover:text-foreground',
        a.bgHover,
        active && [
          'border-transparent font-medium',
          a.bgActive,
          a.textActive,
          // Left accent bar — 2px pill inset by 4px vertically, tinted per section.
          "before:absolute before:left-0 before:top-1 before:bottom-1 before:w-0.5 before:rounded-r-full before:content-['']",
          a.ringActive,
        ],
      )}
    >
      <Icon
        className={cn(
          'size-4 shrink-0 transition-colors',
          active ? a.iconActive : cn('text-foreground/60 group-hover/nav:text-foreground', a.icon),
        )}
        aria-hidden
      />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge != null && item.badge !== 0 && (
        <span
          className={cn(
            'ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full',
            'bg-primary/10 px-1.5 text-2xs font-semibold text-primary',
          )}
        >
          {item.badge}
        </span>
      )}
    </NavLink>
  );

  // Radix Tooltip wrapper — shows the plain-language description on hover
  // so a new operator can peek at what a page is for without clicking.
  // Skip the wrapper entirely when there's nothing useful to say.
  if (!item.description) {
    return <li>{link}</li>;
  }

  return (
    <li>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" align="center" className="max-w-xs px-2.5 py-1.5">
          <div className="text-2xs font-semibold uppercase tracking-wide text-white/70">
            {item.label}
          </div>
          <div className="text-xs font-normal leading-snug text-white">
            {item.description}
          </div>
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/**
 * Turn the raw roles array into a short subtitle (e.g. "Admin", "Sales Manager").
 * Falls back to "Member" if the user has no roles assigned yet.
 */
function roleForDisplay(roles: string[]): string {
  if (!roles || roles.length === 0) return 'Member';
  const first = roles[0]!;
  return first
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((s) => s[0]!.toUpperCase() + s.slice(1))
    .join(' ');
}

/**
 * Read the persisted open state for a single group.
 * Returns `undefined` when nothing was ever stored — the caller should then
 * fall back to `defaultExpanded` / active-route detection.
 */
function readGroupPref(groupLabel: string, userId: number | string): boolean | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(expandedKey(groupLabel, userId));
    if (raw == null) return undefined;
    return raw === 'true';
  } catch {
    return undefined;
  }
}

/** Same shape as `readGroupPref`, but for a second-level subgroup. */
function readSubgroupPref(
  groupLabel: string,
  subgroupLabel: string,
  userId: number | string,
): boolean | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(subgroupKey(groupLabel, subgroupLabel, userId));
    if (raw == null) return undefined;
    return raw === 'true';
  } catch {
    return undefined;
  }
}

/**
 * Compute the initial `{ [groupLabel]: boolean }` open map for a fresh mount:
 * respect any per-user stored preference, otherwise default to closed except
 * for groups that either hold the active route or declare `defaultExpanded`.
 */
function buildInitialOpenMap(
  groups: readonly NavGroup[],
  userId: number | string,
  activeGroupLabel: string | null,
): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const g of groups) {
    const stored = readGroupPref(g.label, userId);
    if (stored !== undefined) {
      map[g.label] = stored;
      continue;
    }
    map[g.label] = Boolean(g.defaultExpanded) || g.label === activeGroupLabel;
  }
  // Enforce accordion invariant on first mount too: at most one group open.
  // The active-route group wins; then defaultExpanded; then whichever came
  // first in the nav order. Storage keeping several groups open from a
  // previous non-accordion build gets collapsed to one here.
  const openLabels = Object.keys(map).filter((k) => map[k]);
  if (openLabels.length > 1) {
    const keeper =
      (activeGroupLabel && openLabels.includes(activeGroupLabel) ? activeGroupLabel : null) ??
      openLabels.find((l) => groups.find((g) => g.label === l && g.defaultExpanded)) ??
      openLabels[0]!;
    for (const label of openLabels) map[label] = label === keeper;
  }
  return map;
}
